import type { ReactNode } from 'react';
import { CoinIcon } from './icons';

export interface LeaderboardRowProps {
  rank: number;
  name: string;
  /** Avatar slot (AvatarPreset or photo). */
  avatar?: ReactNode;
  /** totalBucksEarned. */
  score: number;
  /** Rank change since last refresh: positive = climbed. */
  delta?: number;
  /** SR text for the delta, e.g. t('ec2.a11y.rankUp', {count}). */
  deltaLabel?: string;
  /** The viewer's own row: --primary border, shown with t('ec2.leaderboard.you') as name. */
  isSelf?: boolean;
  /** #1 row: --gradient-flare left edge (the screen's single gradient allowance). */
  flareEdge?: boolean;
  locale?: string;
  className?: string;
}

/**
 * Leaderboard row (brief §6): tabular rank, ▲/▼ delta (--success/--danger — falling
 * rows get no flourish elsewhere), name, gold tabular score. FLIP animation is the
 * app's job (framer `layout`); this row is layout-animation friendly (stable height).
 */
export function LeaderboardRow({
  rank,
  name,
  avatar,
  score,
  delta,
  deltaLabel,
  isSelf = false,
  flareEdge = false,
  locale = 'en',
  className = '',
}: LeaderboardRowProps) {
  const formatted = new Intl.NumberFormat(locale).format(score);
  return (
    <div
      data-testid="ec-leaderboard-row"
      data-self={isSelf || undefined}
      className={
        `relative flex h-14 items-center gap-3 overflow-hidden rounded-(--radius-chip) bg-surface px-3 ` +
        `${isSelf ? 'border-[1.5px] border-primary' : 'border border-border'} ${className}`
      }
    >
      {flareEdge && (
        <span aria-hidden data-testid="ec-flare-edge" className="absolute inset-y-0 left-0 w-1" style={{ background: 'var(--gradient-flare)' }} />
      )}
      <span className="w-7 text-right font-display text-base font-[650] tabular-nums text-fg">{rank}</span>
      <span className="w-9 text-[13px] font-semibold tabular-nums">
        {delta !== undefined && delta !== 0 && (
          <>
            <span aria-hidden className={delta > 0 ? 'text-success' : 'text-danger'}>
              {delta > 0 ? '▲' : '▼'}
              {Math.abs(delta)}
            </span>
            {deltaLabel && <span className="sr-only">{deltaLabel}</span>}
          </>
        )}
      </span>
      {avatar && <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full">{avatar}</span>}
      <span className={`min-w-0 flex-1 truncate text-base ${isSelf ? 'font-semibold text-fg' : 'text-fg'}`}>{name}</span>
      <span className="inline-flex items-center gap-1.5 font-display text-lg font-[750] tabular-nums text-bucks">
        <span>{formatted}</span>
        <CoinIcon size={14} aria-hidden />
      </span>
    </div>
  );
}
